import { useMemo, useRef } from "react";

/**
 * CEO 2026-10-25 (F2026-00045: "o Gestion não me permite ver o PDF") — o
 * navegador bloqueia `window.open` chamado DEPOIS de esperar o servidor (o
 * clique já "expirou" e vira pop-up). A aba é aberta NO CLIQUE, vazia, e o
 * arquivo é carregado nela quando o link chega.
 */
export interface AbaPendente {
  /** Carrega o link na aba aberta no clique (ou abre uma, se o navegador barrou). */
  ir: (url: string | null | undefined) => void;
  /** Fecha a aba vazia (erro ou nada a abrir). */
  fechar: () => void;
}

export function abrirAbaPendente(): AbaPendente {
  let aba: Window | null = null;
  try {
    aba = window.open("", "_blank");
  } catch {
    aba = null;
  }
  if (aba) {
    try {
      aba.document.title = "…";
      aba.document.body.style.cssText =
        "margin:0;display:flex;align-items:center;justify-content:center;height:100vh;font-family:system-ui,sans-serif;color:#64748b;background:#f8fafc";
      aba.document.body.textContent = "⏳";
    } catch {
      /* aba de outra origem — segue sem o aviso */
    }
  }
  return {
    ir(url) {
      if (!url) {
        this.fechar();
        return;
      }
      if (aba && !aba.closed) {
        try {
          aba.opener = null;
        } catch {
          /* ignora */
        }
        aba.location.href = url;
        return;
      }
      // Bloqueada até no clique: tenta por um link (alguns navegadores deixam).
      const a = document.createElement("a");
      a.href = url;
      a.target = "_blank";
      a.rel = "noopener noreferrer";
      document.body.appendChild(a);
      a.click();
      a.remove();
    },
    fechar() {
      try {
        aba?.close();
      } catch {
        /* ignora */
      }
    },
  };
}

/** Abre a aba no clique e carrega nela o link que `obterUrl` devolver. */
export async function abrirArquivo(
  obterUrl: () => Promise<string | null | undefined>,
): Promise<void> {
  const aba = abrirAbaPendente();
  try {
    aba.ir(await obterUrl());
  } catch (e) {
    aba.fechar();
    throw e;
  }
}

/**
 * Para PDFs gerados por mutação (worklog, OR, relatório): `abrir()` no clique,
 * antes do `mutate`; `ir(url)` no onSuccess; `fechar()` no onError.
 */
export function useAbaPendente(): {
  abrir: () => void;
  ir: (url: string | null | undefined) => void;
  fechar: () => void;
} {
  const ref = useRef<AbaPendente | null>(null);
  return useMemo(
    () => ({
      abrir() {
        ref.current?.fechar();
        ref.current = abrirAbaPendente();
      },
      ir(url: string | null | undefined) {
        const aba = ref.current;
        ref.current = null;
        if (aba) aba.ir(url);
        else if (url) window.open(url, "_blank", "noopener");
      },
      fechar() {
        ref.current?.fechar();
        ref.current = null;
      },
    }),
    [],
  );
}
